import React, { useEffect, useState, useCallback } from 'react';
import Select from 'react-select';

import { hasRoles, mergeRoles, CREATE_FORM_ROLES, EDIT_FORM_ROLES } from '../../../../util/RoleUtil';

import { retrieveStudyDropdownsSpecific, retrieveStudySchedulesDropdowns } from '../../../../services/StudyService';
import { createForm, deleteForm, updateForm } from '../../../../services/FormService';

import { BackButton } from '../../../common/button/back-button/BackButton';
import { SaveButton } from '../../../common/button/save-button/SaveButton';
import { ToolTip } from '../../../common/tool-tip/ToolTip';
import { Checkbox } from '../../../common/checkbox/Checkbox';
import { DeleteFormModal } from './DeleteFormModal';

import './CreateNewForm.css';

const roles = mergeRoles(CREATE_FORM_ROLES, EDIT_FORM_ROLES);

export const CreateNewForm = ({ context, navigateTo, setSpinnerText, showToaster, toolTipsEnabled }) => {
  const [formName, setFormName] = useState('');
  const [study, setStudy] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [isStudySpecific, setIsStudySpecific] = useState(false);
  const [isScheduleSpecific, setIsScheduleSpecific] = useState(false);
  const [isConsentForm, setIsConsentForm] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [sections, setSections] = useState(['']);
  const [studies, setStudies] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [formNameError, setFormNameError] = useState('');
  const [studySpecificError, setStudySpecificError] = useState('');
  const [scheduleSpecificError, setScheduleSpecificError] = useState('');
  const [isRemoveFormModalOpen, setIsRemoveFormModalOpen] = useState(false);

  const fieldsDisabled = !hasRoles(context, roles);

  const fetchStudies = useCallback(async () => {
    setSpinnerText('Retrieving Studies');
    const [data, error] = await retrieveStudyDropdownsSpecific(context.username, true);
    if (data) {
      if (data.responseStatus === 'Success') {
        const formattedStudies = data.dropdownStudyVOs.map((s) => ({
          value: s,
          label: s.name,
        }));
        setStudies(formattedStudies);
        setSpinnerText(null);
        return formattedStudies;
      }
      showToaster(data);
    } else {
      showToaster({ responseStatus: 'Error', responseMessage: error.message });
    }
    setSpinnerText(null);
    return [];
  }, [context.username, setSpinnerText, showToaster]);

  const fetchSchedules = useCallback(
    async (studyExternalReference) => {
      if (!studyExternalReference) {
        setSchedules([]);
        return [];
      }
      setSpinnerText('Retrieving Schedules');
      const [data, error] = await retrieveStudySchedulesDropdowns(studyExternalReference, context.username, false);
      if (data) {
        if (data.responseStatus === 'Success') {
          const formattedSchedules = data.dropdownStudyScheduleVOS.map((s) => ({
            value: s,
            label: s.name,
          }));
          setSchedules(formattedSchedules);
          setSpinnerText(null);
          return formattedSchedules;
        }
        showToaster(data);
      } else {
        showToaster({ responseStatus: 'Error', responseMessage: error.message });
      }
      setSpinnerText(null);
      return [];
    },
    [context.username, setSpinnerText, showToaster]
  );

  const handleIsStudySpecificChange = useCallback(
    async (value) => {
      setIsStudySpecific(value);
      setStudySpecificError('');
      if (!value) {
        setStudy(null);
        setIsScheduleSpecific(false);
        setSchedule(null);
        setSchedules([]);
        setScheduleSpecificError('');
        return;
      }
      await fetchStudies();
    },
    [fetchStudies]
  );

  const handleIsScheduleSpecificChange = useCallback(
    async (value) => {
      setIsScheduleSpecific(value);
      setScheduleSpecificError('');
      if (!value) {
        setSchedule(null);
        setSchedules([]);
        return;
      }
      setStudy((currentStudy) => {
        if (currentStudy?.value?.studyExternalReference) {
          fetchSchedules(currentStudy.value.studyExternalReference);
        } else {
          setScheduleSpecificError('*SELECT STUDY FIRST');
        }
        return currentStudy;
      });
    },
    [fetchSchedules]
  );

  const handleStudySelection = async (selectedStudyOption) => {
    setStudy(selectedStudyOption);
    setStudySpecificError('');
    setSchedule(null);
    setSchedules([]);
    setScheduleSpecificError('');
    if (isScheduleSpecific && selectedStudyOption?.value?.studyExternalReference) {
      await fetchSchedules(selectedStudyOption.value.studyExternalReference);
    }
  };

  const handleScheduleSelection = (selectedScheduleOption) => {
    setSchedule(selectedScheduleOption);
    setScheduleSpecificError('');
  };

  useEffect(() => {
    const form = context.additionalContext?.form;
    if (!form) return;

    setEditMode(true);
    setFormName(form.formName || '');
    setIsConsentForm(!!form.consentForm);
    setTotalPages(form.totalPages || 0);
    setSections(form.sections?.length > 0 ? form.sections : ['']);

    if (form.studySpecific) {
      setIsStudySpecific(true);
      setStudy({
        value: { studyExternalReference: form.studyExternalReference },
        label: form.studyName,
      });
      fetchStudies();

      const isSchedule = !!form.studyScheduleExternalReference;

      if (isSchedule) {
        setIsScheduleSpecific(true);
        setSchedule({
          value: { studyScheduleExternalReference: form.studyScheduleExternalReference },
          label: form.studyScheduleName,
        });
        fetchSchedules(form.studyExternalReference);
      } else {
        setIsScheduleSpecific(false);
        setSchedule(null);
      }
    } else {
      setIsStudySpecific(false);
      setStudy(null);
      setIsScheduleSpecific(false);
      setSchedule(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.additionalContext?.form]);

  const handleBack = () => navigateTo('formAdmin');

  const handleAddSectionClick = () => setSections((prev) => [...prev, '']);

  const handleSectionInputChange = (index, value) => {
    setSections((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleDeleteSectionClick = (index) => {
    setSections((prev) => prev.filter((_, i) => i !== index));
  };

  const validateRequiredFields = () => {
    let isValid = true;

    if (!formName) {
      setFormNameError('*REQUIRED');
      isValid = false;
    } else {
      setFormNameError('');
    }

    if (isStudySpecific) {
      if (!study || !study.label) {
        setStudySpecificError('*REQUIRED');
        isValid = false;
      } else {
        setStudySpecificError('');
      }
    } else {
      setStudySpecificError('');
    }

    if (isScheduleSpecific) {
      if (!schedule || !schedule.label) {
        setScheduleSpecificError('*REQUIRED');
        isValid = false;
      } else {
        setScheduleSpecificError('');
      }
    } else {
      setScheduleSpecificError('');
    }

    return isValid;
  };

  const buildFormPayload = (base = {}) => ({
    form: {
      ...base,
      formName,
      studySpecific: isStudySpecific,
      scheduleSpecific: isStudySpecific && isScheduleSpecific,
      consentForm: isConsentForm,
      studyExternalReference: isStudySpecific ? study?.value?.studyExternalReference || null : null,
      studyScheduleExternalReference:
        isStudySpecific && isScheduleSpecific ? schedule?.value?.studyScheduleExternalReference || null : null,
      totalPages,
      sections,
    },
  });

  const handleSaveClick = async () => {
    if (!validateRequiredFields()) return;

    if (editMode) {
      const updatedForm = buildFormPayload(context.additionalContext.form);
      await update(updatedForm);
    } else {
      const formToSave = buildFormPayload();
      await saveForm(formToSave);
    }
  };

  const saveForm = async (form) => {
    setSpinnerText('Saving Form');
    const [data, error] = await createForm(context.username, form);
    if (data) {
      if (data.responseStatus === 'Success') {
        showToaster({ responseStatus: 'Success', responseMessage: 'Form Created' });
        navigateTo('formAdmin');
      } else if (data.responseMessage === 'Form Name already in Use.') {
        setFormNameError(data.responseMessage);
      } else {
        showToaster(data);
      }
    } else {
      showToaster({ responseStatus: 'Error', responseMessage: error.message });
    }
    setSpinnerText(null);
  };

  const update = async (form) => {
    setSpinnerText('Updating Form');
    const [data, error] = await updateForm(context.username, form);
    if (data) {
      if (data.responseStatus === 'Success') {
        showToaster({ responseStatus: 'Success', responseMessage: 'Form Updated' });
        navigateTo('formAdmin');
      } else {
        showToaster(data);
      }
    } else {
      showToaster({ responseStatus: 'Error', responseMessage: error.message });
    }
    setSpinnerText(null);
  };

  const handleDeleteClick = () => {
    setIsRemoveFormModalOpen(true);
  };

  const handleDeleteConfirmationClick = () => {
    const formToDelete = {
      formExternalReference: context.additionalContext.form.formExternalReference,
      studyExternalReference: context.additionalContext.form.studyExternalReference,
    };
    formDelete(formToDelete).then(() => navigateTo('formAdmin'));
  };

  const closeRemoveFormModal = () => {
    setIsRemoveFormModalOpen(false);
  };

  const formDelete = async (form) => {
    setSpinnerText('Deleting Form');
    const [data, error] = await deleteForm(context.username, form);
    if (data) {
      if (data.responseStatus === 'Success') {
        showToaster({ responseStatus: 'Success', responseMessage: 'Form Deleted' });
      } else {
        showToaster(data);
      }
    } else {
      showToaster({ responseStatus: 'Error', responseMessage: error.message });
    }
    setSpinnerText(null);
    setIsRemoveFormModalOpen(false);
  };

  return (
    <div className="form-create-container">
      <div className="clients-create-header-container">
        <BackButton onClick={handleBack} />
        <h2>{editMode ? 'Update Form' : 'Create New Form'}</h2>
      </div>

      <div className="tab-container">
        <div className="form-create-general-tab-input-container">
          <label className={'input-label-sm'}>Form Name:</label>
          <input
            className={`text-input ${formNameError && 'error-border'}`}
            type="text"
            value={formName}
            disabled={editMode && fieldsDisabled}
            onChange={({ target }) => setFormName(target.value)}
          />
          {toolTipsEnabled && (
            <div className="form-tooltips">
              <ToolTip
                tooltipExternalReference={'formName123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'Form Name:'}
              />
            </div>
          )}
        </div>
        <div className="form-error-message">
          {formNameError && <div className="small-red-text">{formNameError}</div>}
        </div>

        <div className="form-checkbox-section">
          <Checkbox
            leftLabel={'Study Specific:'}
            checked={isStudySpecific}
            onChange={handleIsStudySpecificChange}
            leftLabelFlex="0 10 150px"
            disabled={editMode && fieldsDisabled}
            leftLabelFontSize="16px"
          />
          {isStudySpecific && (
            <Select
              options={studies}
              isClearable
              closeMenuOnSelect={true}
              onChange={handleStudySelection}
              isDisabled={editMode && fieldsDisabled}
              value={study}
              className={`input-select${studySpecificError ? 'error-border' : ''}`}
              classNamePrefix={`input-select${studySpecificError ? 'error-border' : ''}`}
            />
          )}
          {toolTipsEnabled && (
            <div className="form-tooltips">
              <ToolTip
                tooltipExternalReference={'isStudySpecific123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'Study Specific:'}
              />
            </div>
          )}
        </div>
        <div className="form-error-message">
          {studySpecificError && <div className="small-red-text">{studySpecificError}</div>}
        </div>

        {isStudySpecific && (
          <>
            <div className="form-checkbox-section">
              <Checkbox
                leftLabel={'Schedule Specific:'}
                checked={isScheduleSpecific}
                onChange={handleIsScheduleSpecificChange}
                leftLabelFlex="0 10 150px"
                disabled={editMode && fieldsDisabled}
                leftLabelFontSize="16px"
              />
              {isScheduleSpecific && (
                <Select
                  options={schedules}
                  isClearable
                  closeMenuOnSelect={true}
                  onChange={handleScheduleSelection}
                  isDisabled={editMode && fieldsDisabled}
                  value={schedule}
                  className={`input-select${scheduleSpecificError ? 'error-border' : ''}`}
                  classNamePrefix={`input-select${scheduleSpecificError ? 'error-border' : ''}`}
                />
              )}
              {toolTipsEnabled && (
                <div className="form-tooltips">
                  <ToolTip
                    tooltipExternalReference={'isScheduleSpecific123'}
                    context={context}
                    setSpinnerText={setSpinnerText}
                    showToaster={showToaster}
                    fieldName={'Schedule Specific:'}
                  />
                </div>
              )}
            </div>
            <div className="form-error-message">
              {scheduleSpecificError && <div className="small-red-text">{scheduleSpecificError}</div>}
            </div>
          </>
        )}

        <div className="form-checkbox-section">
          <Checkbox
            leftLabel={'Consent Form:'}
            checked={isConsentForm}
            leftLabelFlex="0 10 150px"
            leftLabelFontSize="16px"
            disabled={editMode && fieldsDisabled}
            onChange={setIsConsentForm}
          />
          {toolTipsEnabled && (
            <div className="form-tooltips">
              <ToolTip
                tooltipExternalReference={'isConsentForm123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'Consent Form:'}
              />
            </div>
          )}
        </div>

        <div className="form-create-general-tab-input-container">
          <label className={'input-label-sm'}>Total Pages:</label>
          <input
            className="text-input"
            type="number"
            value={totalPages}
            disabled={editMode && fieldsDisabled}
            onChange={({ target }) => setTotalPages(target.value)}
          />
          {toolTipsEnabled && (
            <div className="form-tooltips">
              <ToolTip
                tooltipExternalReference={'totalPages123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'Total Pages:'}
              />
            </div>
          )}
        </div>

        <div className="form-create-general-tab-input-container">
          <label className={'input-label-sm'}>Sections:</label>
          <div className="form-create-general-tab-input-container-column">
            {sections.map((section, index) => (
              <div key={index} className="form-create-section-input-container">
                <input
                  className="text-input"
                  type="text"
                  value={section}
                  disabled={editMode && fieldsDisabled}
                  onChange={({ target }) => handleSectionInputChange(index, target.value)}
                />
                {sections.length > 1 && (
                  <button className="delete-section-button" onClick={() => handleDeleteSectionClick(index)}>
                    X
                  </button>
                )}
              </div>
            ))}
          </div>
          {toolTipsEnabled && (
            <div className="form-tooltips">
              <ToolTip
                tooltipExternalReference={'sections123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'Sections:'}
              />
            </div>
          )}
        </div>

        <div className="form-add-button">
          <SaveButton
            title={'Add Section'}
            onClick={handleAddSectionClick}
            color={'#74A47FFF'}
            disabled={editMode && fieldsDisabled}
          />
        </div>
      </div>

      {isRemoveFormModalOpen && (
        <DeleteFormModal closeModal={closeRemoveFormModal} handleConfirmRemoveForm={handleDeleteConfirmationClick} />
      )}

      <div className={'form-button-container'}>
        {editMode ? (
          <>
            <SaveButton
              color={'#ffffff'}
              textColor={'#077187'}
              border={'1px solid #077187'}
              title={'Delete Form'}
              onClick={handleDeleteClick}
              disabled={editMode && fieldsDisabled}
            />
            <SaveButton
              color={'#077187'}
              title={'Save Changes'}
              onClick={handleSaveClick}
              disabled={editMode && fieldsDisabled}
            />
          </>
        ) : (
          <SaveButton
            color={'#077187'}
            title={'Save Changes'}
            onClick={handleSaveClick}
            disabled={editMode && fieldsDisabled}
          />
        )}
      </div>
    </div>
  );
};
